/**
 * DJI Fly's waypoint folder on a controller or phone attached as a drive, reached through the
 * browser's File System Access API (Chrome/Edge on a desktop). The folder is asked for once and
 * its handle remembered in IndexedDB.
 *
 * DJI Fly lists only missions it created itself (it keeps them in its own database), so a plan
 * can only replace an existing mission: `<uuid>/<uuid>.kmz` plus a `<name>.txt` carrying the
 * mission name, and `map_preview/<uuid>/<uuid>.jpg` for the thumbnail.
 */

type Permission = 'granted' | 'denied' | 'prompt';

export interface WritableLike {
  write(data: Blob): Promise<void>;
  close(): Promise<void>;
}
export interface FileHandleLike {
  kind: 'file';
  name: string;
  getFile(): Promise<File>;
  createWritable(): Promise<WritableLike>;
}
export interface DirectoryHandleLike {
  kind: 'directory';
  name: string;
  queryPermission(opts: { mode: 'readwrite' }): Promise<Permission>;
  requestPermission(opts: { mode: 'readwrite' }): Promise<Permission>;
  values(): AsyncIterable<DirectoryHandleLike | FileHandleLike>;
  getDirectoryHandle(name: string, opts?: { create: boolean }): Promise<DirectoryHandleLike>;
  getFileHandle(name: string, opts?: { create: boolean }): Promise<FileHandleLike>;
  removeEntry(name: string): Promise<void>;
}
export interface HandleStore {
  get(): Promise<DirectoryHandleLike | null>;
  set(handle: DirectoryHandleLike): Promise<void>;
}

declare global {
  interface Window {
    showDirectoryPicker?: (opts?: { mode?: 'read' | 'readwrite'; id?: string }) => Promise<DirectoryHandleLike>;
  }
}

const DB = 'forestum-missions';
const STORE = 'handles';
const KEY = 'dji-fly-folder';

export function supportsFolderSave(): boolean {
  return typeof window !== 'undefined' && typeof window.showDirectoryPicker === 'function';
}

export function pickFolder(): Promise<DirectoryHandleLike> {
  if (!window.showDirectoryPicker) throw new Error('unsupported');
  return window.showDirectoryPicker({ mode: 'readwrite', id: KEY });
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(STORE);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

/** Handles survive only in IndexedDB (they are structured-cloneable, not serialisable); without it nothing is remembered. */
export const indexedDbStore: HandleStore = {
  async get() {
    if (typeof indexedDB === 'undefined') return null;
    try {
      const db = await openDb();
      return await new Promise((resolve) => {
        const req = db.transaction(STORE).objectStore(STORE).get(KEY);
        req.onsuccess = () => resolve((req.result as DirectoryHandleLike | undefined) ?? null);
        req.onerror = () => resolve(null);
      });
    } catch {
      return null;
    }
  },
  async set(handle) {
    if (typeof indexedDB === 'undefined') return;
    try {
      const db = await openDb();
      await new Promise<void>((resolve) => {
        const tx = db.transaction(STORE, 'readwrite');
        tx.objectStore(STORE).put(handle, KEY);
        tx.oncomplete = () => resolve();
        tx.onerror = () => resolve();
      });
    } catch {
      // A blocked store only means asking for the folder again next time.
    }
  },
};

async function usable(handle: DirectoryHandleLike | null): Promise<DirectoryHandleLike | null> {
  if (!handle) return null;
  if ((await handle.queryPermission({ mode: 'readwrite' })) === 'granted') return handle;
  return (await handle.requestPermission({ mode: 'readwrite' })) === 'granted' ? handle : null;
}

/** The remembered waypoint folder, or the picker when none is usable; `force` always asks. */
export async function waypointFolder(
  force = false,
  deps: { store: HandleStore; pick: () => Promise<DirectoryHandleLike> } = { store: indexedDbStore, pick: pickFolder },
): Promise<DirectoryHandleLike> {
  const remembered = force ? null : await usable(await deps.store.get());
  if (remembered) return remembered;
  const picked = await deps.pick();
  await deps.store.set(picked);
  return picked;
}

export interface DjiMission {
  uuid: string;
  /** From the `<name>.txt` DJI Fly keeps beside the KMZ; null when there is none. */
  name: string | null;
  /** The KMZ's modification time, ms since the epoch. */
  modified: number;
  preview: Blob | null;
}

async function entries(dir: DirectoryHandleLike): Promise<(DirectoryHandleLike | FileHandleLike)[]> {
  const out: (DirectoryHandleLike | FileHandleLike)[] = [];
  for await (const e of dir.values()) out.push(e);
  return out;
}

async function previewOf(root: DirectoryHandleLike, uuid: string): Promise<Blob | null> {
  try {
    const file = await (await (await root.getDirectoryHandle('map_preview')).getDirectoryHandle(uuid)).getFileHandle(`${uuid}.jpg`);
    return await file.getFile();
  } catch {
    return null;
  }
}

/** The missions DJI Fly knows: every folder holding a KMZ named after itself, newest first. */
export async function listDjiMissions(root: DirectoryHandleLike): Promise<DjiMission[]> {
  const missions: DjiMission[] = [];
  for (const dir of await entries(root)) {
    if (dir.kind !== 'directory') continue;
    const files = (await entries(dir)).filter((e): e is FileHandleLike => e.kind === 'file');
    const kmz = files.find((f) => f.name === `${dir.name}.kmz`);
    if (!kmz) continue;
    const txt = files.find((f) => f.name.toLowerCase().endsWith('.txt'));
    missions.push({
      uuid: dir.name,
      name: txt ? txt.name.replace(/\.txt$/i, '') : null,
      modified: (await kmz.getFile()).lastModified,
      preview: await previewOf(root, dir.name),
    });
  }
  return missions.sort((a, b) => b.modified - a.modified);
}

/**
 * Chrome writes into a `.crswap` swap file and renames it over the target on close. On an MTP
 * mount a rename over an existing file reports success, drops the target and leaves the swap,
 * while a rename to a fresh name works. `getFileHandle(create)` itself creates an empty target,
 * so the handle is taken first and the file removed before the writable opens; Chrome then
 * renames the swap onto a name that no longer exists. Every step costs a few seconds over MTP.
 */
async function writeFile(dir: DirectoryHandleLike, name: string, data: Blob) {
  const handle = await dir.getFileHandle(name, { create: true });
  await dir.removeEntry(name).catch(() => undefined);
  const w = await handle.createWritable();
  await w.write(data);
  await w.close();
}

/**
 * Replaces an existing DJI Fly mission's KMZ, name file (`<name>.txt`, holding the summary) and
 * preview; its other files stay.
 */
export async function writeDjiMission(
  root: DirectoryHandleLike,
  uuid: string,
  files: { kmz: Blob; name: string; summary: string; preview: Blob | null },
): Promise<{ path: string }> {
  const dir = await root.getDirectoryHandle(uuid);
  for (const e of await entries(dir)) {
    // Old name files and swap files left by an interrupted write would confuse DJI Fly.
    if (e.kind === 'file' && /\.(txt|crswap)$/i.test(e.name)) await dir.removeEntry(e.name);
  }
  await writeFile(dir, `${uuid}.kmz`, files.kmz);
  const safeName = files.name.replace(/[\\/:*?"<>|]/g, '-').trim() || uuid;
  await writeFile(dir, `${safeName}.txt`, new Blob([files.summary], { type: 'text/plain' }));
  if (files.preview) {
    const previews = await root.getDirectoryHandle('map_preview', { create: true });
    await writeFile(await previews.getDirectoryHandle(uuid, { create: true }), `${uuid}.jpg`, files.preview);
  }
  return { path: `${uuid}/${uuid}.kmz` };
}
